<script setup>
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useProductConfigurationStore } from '@/stores/productConfiguration';

const store = useProductConfigurationStore();
const { search } = store;
const { all } = storeToRefs(store);

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchItems = () => search({ ...params.value });

const clearSearch = () => {
    params.value.term = '';
    searchItems();
};

const goCreate = () => router.visit('/product-configurations/create');

const goView = (item) => router.visit(`/product-configurations/${item.hash}`);

const goClone = (item) => router.visit(`/product-configurations/${item.hash}/clone`);

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchItems();
};

const formatMST = (utcDate) => {
    if (!utcDate) {
        return '';
    }

    const date = new Date(utcDate);

    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Edmonton',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date) + ' (MST)';
};

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>

    <template #title>{{ $t('label.product_configuration') }}</template>

    <template #actions>
        <StyledButton @click="goCreate">
            {{ $t('label.new_product') }}
        </StyledButton>
    </template>

    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchItems"
            name="search"
            :label="$t('label.product_configuration_search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
    </div>

    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.product_title'), key: 'title', value: item => item.title ?? '', sortable: false },
            { title: $t('label.version'), key: 'version', value: item => item.version ?? '', sortable: false },
            { title: $t('label.last_updated'), key: 'updated_at',     value: item => formatMST(item.updated_at), sortable: false },
            { title: $t('label.status'), key: 'is_active', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: $t('label.view'), icon: 'eye', action: goView },
            { title: $t('label.clone'), icon: 'copy', action: goClone },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
